// Renderers of the advanced components (7.6E), shared by the Studio canvas
// (editor: static, no controls, nothing plays) and the interactive preview
// (driven by the PreviewEngine through `ComponentHost`). Same rules as the
// rest of the renderer: typed document values only, text as text nodes, no
// HTML from the document, no network beyond the pinned asset URLs.
//
// The real <video>/<audio> elements never decide anything: the engine's
// logical state does, and `syncMedia` makes the elements follow it.
//
// Runtime-owned UI (media controls, carousel arrows/dots/pause, survey
// "Siguiente", field error text) is part of each component's contract and
// carries its own accessible names.

import type {
  AssetRef,
  CarouselProps,
  CountdownProps,
  CreativeDocument,
  Element,
  FormField,
  ProgressProps,
  SurveyQuestion,
  SurveyQuestionProps,
} from "@interactads/creative-model";
import { ChevronLeft, ChevronRight, Film, Music, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useRef, type CSSProperties, type ReactNode } from "react";
import { countdownText, plain } from "@interactads/creative-runtime/ui-text";
import type { CarouselView, MediaView, SurveyView } from "../preview/engine";

// Shared with the served ad since 7.6I (ADR-0029): one implementation of the runtime UI text,
// the media following and the countdown digits.
export { countdownText, plain } from "@interactads/creative-runtime/ui-text";
export { syncCountdowns, syncMedia } from "@interactads/creative-runtime/media-sync";

/** What the preview engine exposes to the renderer (the editor passes none). */
export interface ComponentHost {
  readonly mediaView: (id: string) => MediaView | undefined;
  readonly mediaControl: (id: string, action: "toggle" | "mute" | "unmute") => void;
  readonly carouselView: (id: string) => CarouselView;
  readonly carouselNavigate: (id: string, to: "next" | "prev" | number, via: "swipe" | "arrow" | "dot") => void;
  readonly carouselHold: (id: string, held: boolean) => void;
  readonly carouselTogglePause: (id: string) => void;
  readonly surveyView: () => SurveyView;
  readonly surveyChoose: (choice: string | number) => void;
  readonly surveyNext: () => void;
  readonly surveyCanContinue: () => boolean;
  readonly fieldValue: (form: string, field: string) => string[] | number | string | undefined;
  readonly fieldError: (form: string, field: string) => boolean;
  readonly fieldInput: (elementId: string, value: string[] | number | string) => void;
  readonly timerRemaining: (id: string) => number;
  readonly variable: (id: string) => number | boolean | string | undefined;
}

/** Editor-only choices of what to show for a component (ephemeral; never persisted). */
export interface EditorFocus {
  readonly slide: (carouselId: string) => number;
  readonly question: () => string | undefined;
}

const fmt = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1_000));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
};

const controlBtn =
  "pointer-events-auto grid size-7 place-items-center rounded-full bg-black/65 text-white outline-offset-2 focus-visible:outline-2 focus-visible:outline-white [&_svg]:size-3.5";

// ─── Video ─────────────────────────────────────────────────────────────────

export function VideoContent({
  el,
  fit,
  mode,
  url,
  posterUrl,
  captionsUrl,
  host,
}: {
  el: Extract<Element, { type: "video" }>;
  fit: string;
  mode: "editor" | "preview";
  url: string | undefined;
  posterUrl: string | undefined;
  /** WebVTT captions asset of the document (parsed by the browser as text). */
  captionsUrl?: string | undefined;
  host?: ComponentHost | undefined;
}) {
  const label = el.interactive?.label ?? el.name ?? "Video";
  const fill: CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: fit as CSSProperties["objectFit"],
    display: "block",
  };
  if (mode === "editor" || !host) {
    // Static in the editor: the poster, else the first frame (media fragment), never playing.
    if (posterUrl) return <img src={posterUrl} alt="" draggable={false} style={{ ...fill, pointerEvents: "none" }} />;
    return url ? (
      <div className="relative h-full w-full bg-black">
        <video
          src={`${url}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          style={{ ...fill, pointerEvents: "none" }}
        />
        <span className="pointer-events-none absolute left-1 top-1 flex items-center gap-1 rounded-sm bg-black/60 px-1 text-[10px] text-white">
          <Film className="size-3" aria-hidden="true" /> Video
        </span>
      </div>
    ) : (
      <div className="grid h-full w-full place-items-center bg-black text-[10px] text-white/80">
        Video no disponible
      </div>
    );
  }
  const view = host.mediaView(el.id);
  const controls = el.playback.controls ?? "none";
  const showPoster =
    posterUrl && view && ((view.fresh && !view.playing) || (view.ended && el.playback.endBehavior === "poster"));
  return (
    <div className="relative h-full w-full bg-black">
      <video
        data-media={el.id}
        src={url}
        poster={posterUrl}
        muted
        playsInline
        preload="auto"
        aria-label={label}
        style={fill}
      >
        {captionsUrl ? <track kind="captions" src={captionsUrl} default /> : null}
      </video>
      {showPoster ? <img src={posterUrl} alt="" draggable={false} className="absolute inset-0" style={fill} /> : null}
      {view &&
      (view.blocked || (!view.playing && controls === "none" && view.fresh && el.playback.autoplay !== "none")) ? (
        <button
          type="button"
          className="absolute inset-0 m-auto grid size-12 place-items-center rounded-full bg-black/70 text-white focus-visible:outline-2 focus-visible:outline-white [&_svg]:size-6"
          aria-label={`Reproducir ${label}`}
          onClick={() => {
            host.mediaControl(el.id, "toggle");
          }}
        >
          <Play aria-hidden="true" />
        </button>
      ) : null}
      {view && controls !== "none" ? (
        <div className="pointer-events-none absolute inset-x-1 bottom-1 flex items-center gap-1">
          <button
            type="button"
            className={controlBtn}
            aria-label={view.playing ? `Pausar ${label}` : `Reproducir ${label}`}
            onClick={() => {
              host.mediaControl(el.id, "toggle");
            }}
          >
            {view.playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          </button>
          <button
            type="button"
            className={controlBtn}
            aria-label={view.muted ? "Activar sonido" : "Silenciar"}
            onClick={() => {
              host.mediaControl(el.id, view.muted ? "unmute" : "mute");
            }}
          >
            {view.muted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
          </button>
          <span
            className="ml-auto rounded-sm bg-black/60 px-1 text-[10px] text-white tabular-nums"
            data-testid="media-time"
          >
            {fmt(view.position)} / {fmt(view.duration)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Audio ─────────────────────────────────────────────────────────────────

export function AudioContent({
  el,
  mode,
  url,
  host,
}: {
  el: Extract<Element, { type: "audio" }>;
  mode: "editor" | "preview";
  url: string | undefined;
  host?: ComponentHost | undefined;
}) {
  const label = el.interactive?.label ?? el.name ?? "Audio";
  if (mode === "editor" || !host)
    return (
      <div
        className="grid h-full w-full place-items-center rounded-full border border-dashed border-current/50 bg-black/40 text-white [&_svg]:size-4"
        title={label}
      >
        <Music aria-hidden="true" />
      </div>
    );
  const view = host.mediaView(el.id);
  const controls = (el.playback.controls ?? "none") !== "none";
  const action: "toggle" | "unmute" = view?.playing && view.muted ? "unmute" : "toggle";
  return (
    <>
      <audio data-media={el.id} src={url} preload="auto" muted aria-hidden="true" />
      {controls || view?.blocked ? (
        <button
          type="button"
          className="grid h-full w-full place-items-center rounded-full bg-black/70 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white [&_svg]:size-1/2"
          aria-label={
            action === "unmute"
              ? `Activar el sonido de ${label}`
              : view?.playing
                ? `Pausar ${label}`
                : `Reproducir ${label}`
          }
          data-media-state={view?.playing ? (view.muted ? "muted" : "playing") : "stopped"}
          onClick={() => {
            host.mediaControl(el.id, action);
          }}
        >
          {view?.playing && !view.muted ? (
            <Pause aria-hidden="true" />
          ) : view?.playing ? (
            <VolumeX aria-hidden="true" />
          ) : (
            <Music aria-hidden="true" />
          )}
        </button>
      ) : (
        <span className="sr-only" data-media-state={view?.playing ? "playing" : "stopped"} />
      )}
    </>
  );
}

// ─── Carousel ──────────────────────────────────────────────────────────────

export function CarouselContent({
  el,
  mode,
  host,
  editorIndex,
  renderSlide,
}: {
  el: Extract<Element, { type: "component" }>;
  mode: "editor" | "preview";
  host?: ComponentHost | undefined;
  editorIndex: number;
  renderSlide: (slideId: string) => ReactNode;
}) {
  const props = el.props as unknown as CarouselProps;
  const slides = el.slots?.slides ?? [];
  const live = mode === "preview" && host ? host.carouselView(el.id) : undefined;
  const index = Math.min(slides.length - 1, Math.max(0, live ? live.index : editorIndex));
  const accent = props.accentColor ?? "#111827";
  const label = el.interactive?.label ?? el.name ?? "Carrusel";
  const start = useRef<number | null>(null);
  const interactive = Boolean(live && host);
  const go = (to: "next" | "prev" | number, via: "swipe" | "arrow" | "dot") => {
    host?.carouselNavigate(el.id, to, via);
  };
  return (
    <div
      className="relative h-full w-full overflow-hidden"
      role="region"
      aria-roledescription="carrusel"
      aria-label={label}
      data-carousel-index={index}
      {...(interactive
        ? {
            onPointerEnter: () => {
              host?.carouselHold(el.id, true);
            },
            onPointerLeave: () => {
              host?.carouselHold(el.id, false);
            },
            onFocus: () => {
              host?.carouselHold(el.id, true);
            },
            onBlur: () => {
              host?.carouselHold(el.id, false);
            },
            onPointerDown: (e: React.PointerEvent) => {
              start.current = e.clientX;
            },
            onPointerUp: (e: React.PointerEvent) => {
              const from = start.current;
              start.current = null;
              if (from === null || props.gesture !== "swipe") return;
              const dx = e.clientX - from;
              if (Math.abs(dx) > 30) go(dx < 0 ? "next" : "prev", "swipe");
            },
          }
        : {})}
    >
      {slides.map((id, i) => (
        <div
          key={id}
          role="group"
          aria-roledescription="diapositiva"
          aria-label={`${String(i + 1)} de ${String(slides.length)}`}
          aria-hidden={i !== index}
          inert={i !== index}
          className="absolute inset-0"
          style={
            props.transition === "fade"
              ? { opacity: i === index ? 1 : 0, transition: interactive ? "opacity 300ms ease" : undefined }
              : {
                  transform: `translateX(${String((i - index) * 100)}%)`,
                  transition: interactive ? "transform 300ms ease" : undefined,
                }
          }
        >
          {renderSlide(id)}
        </div>
      ))}
      {slides.length > 1 ? (
        <>
          <button
            type="button"
            className="absolute left-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-full bg-white/85 focus-visible:outline-2 focus-visible:outline-offset-1 [&_svg]:size-4"
            style={{ color: accent, outlineColor: accent }}
            aria-label="Diapositiva anterior"
            tabIndex={interactive ? 0 : -1}
            disabled={interactive && !props.loop && index === 0}
            onClick={() => {
              go("prev", "arrow");
            }}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className="absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-full bg-white/85 focus-visible:outline-2 focus-visible:outline-offset-1 [&_svg]:size-4"
            style={{ color: accent, outlineColor: accent }}
            aria-label="Diapositiva siguiente"
            tabIndex={interactive ? 0 : -1}
            disabled={interactive && !props.loop && index === slides.length - 1}
            onClick={() => {
              go("next", "arrow");
            }}
          >
            <ChevronRight aria-hidden="true" />
          </button>
        </>
      ) : null}
      {props.indicators === "dots" || live?.autoplay ? (
        // A dark pill at the top: readable over any slide and never on top of a bottom caption.
        <div className="absolute inset-x-0 top-1.5 flex justify-center">
          <div className="flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-1">
            {props.indicators === "dots"
              ? slides.map((id, i) => (
                  <button
                    key={id}
                    type="button"
                    className="size-2 rounded-full focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white"
                    style={{ background: i === index ? "#FFFFFF" : "rgb(255 255 255 / 0.45)" }}
                    aria-label={`Ir a la diapositiva ${String(i + 1)}`}
                    aria-current={i === index ? "true" : undefined}
                    tabIndex={interactive ? 0 : -1}
                    onClick={() => {
                      go(i, "dot");
                    }}
                  />
                ))
              : null}
            {live?.autoplay ? (
              <button
                type="button"
                className="ml-0.5 grid size-4 place-items-center rounded-full text-white focus-visible:outline-2 focus-visible:outline-white [&_svg]:size-3"
                aria-label={live.paused ? "Reanudar el carrusel" : "Pausar el carrusel"}
                onClick={() => {
                  host?.carouselTogglePause(el.id);
                }}
              >
                {live.paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Form field (non-PII) ──────────────────────────────────────────────────

export function FieldContent({
  el,
  field,
  mode,
  host,
}: {
  el: Extract<Element, { type: "field" }>;
  field: FormField | undefined;
  mode: "editor" | "preview";
  host?: ComponentHost | undefined;
}) {
  if (!field) return null;
  const a = el.appearance ?? {};
  const size = a.fontSize ?? 13;
  const color = a.textColor ?? "#111827";
  const accent = a.accentColor ?? "#E4002B";
  const live = mode === "preview" && host !== undefined;
  const value = live ? host.fieldValue(el.form, el.field) : undefined;
  const error = live && host.fieldError(el.form, el.field);
  const name = `${el.id}-${field.id}`;
  const label = plain(field.label);
  const box: CSSProperties = { color, fontSize: size, lineHeight: 1.25, accentColor: accent };
  const legend = (
    <legend className="mb-1 font-semibold" style={{ fontSize: size }}>
      {label}
      {field.required ? <span aria-hidden="true"> *</span> : null}
    </legend>
  );
  const errorText = error ? (
    <p role="alert" className="mt-0.5 font-medium" style={{ fontSize: Math.max(10, size - 2), color: "#B91C1C" }}>
      Responde esta pregunta.
    </p>
  ) : null;
  const set = (v: string[] | number | string) => {
    host?.fieldInput(el.id, v);
  };
  switch (field.control) {
    case "radio":
    case "checkbox": {
      const multi = field.control === "checkbox";
      const selected = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
      return (
        <fieldset className="h-full w-full min-w-0 overflow-hidden" style={box}>
          {legend}
          <div
            className="grid gap-1"
            {...(live
              ? {
                  role: multi ? "group" : "radiogroup",
                  "aria-label": label,
                  ...(multi ? {} : { "aria-required": field.required }),
                  ...(error ? { "aria-invalid": true } : {}),
                }
              : {})}
          >
            {field.options.map((o) => {
              const checked = selected.includes(o.id);
              return (
                <label key={o.id} className="flex items-center gap-1.5" style={{ fontSize: size }}>
                  {live ? (
                    <input
                      type={multi ? "checkbox" : "radio"}
                      name={name}
                      checked={checked}
                      onChange={() => {
                        set(multi ? (checked ? selected.filter((x) => x !== o.id) : [...selected, o.id]) : o.id);
                      }}
                      style={{ width: size, height: size }}
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="inline-block shrink-0 border-2"
                      style={{
                        width: size * 0.9,
                        height: size * 0.9,
                        borderColor: accent,
                        borderRadius: multi ? 3 : "50%",
                      }}
                    />
                  )}
                  <span>{plain(o.label)}</span>
                </label>
              );
            })}
          </div>
          {errorText}
        </fieldset>
      );
    }
    case "select":
      return (
        <div className="h-full w-full min-w-0" style={box}>
          <label className="grid gap-1">
            <span className="font-semibold">
              {label}
              {field.required ? <span aria-hidden="true"> *</span> : null}
            </span>
            {live ? (
              <select
                className="rounded border bg-white px-1"
                style={{ borderColor: accent, color: "#111827", fontSize: size, minHeight: size * 2 }}
                value={typeof value === "string" ? value : ""}
                aria-required={field.required}
                aria-invalid={error || undefined}
                onChange={(e) => {
                  set(e.target.value);
                }}
              >
                <option value="">Elige una opción…</option>
                {field.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {plain(o.label)}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className="block rounded border bg-white px-1 text-[#6B7280]"
                style={{ borderColor: accent, minHeight: size * 2 }}
              >
                Elige una opción…
              </span>
            )}
          </label>
          {errorText}
        </div>
      );
    case "rating": {
      const values = Array.from({ length: field.scale.max }, (_, i) => i + 1);
      return (
        <fieldset className="h-full w-full min-w-0" style={box}>
          {legend}
          <div
            className="flex flex-wrap gap-1"
            {...(live
              ? {
                  role: "radiogroup",
                  "aria-label": label,
                  "aria-required": field.required,
                  ...(error ? { "aria-invalid": true } : {}),
                }
              : {})}
          >
            {values.map((v) => {
              const on = value === v;
              return live ? (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={`${String(v)} de ${String(field.scale.max)}`}
                  className="grid place-items-center rounded border-2 font-semibold focus-visible:outline-2 focus-visible:outline-offset-1"
                  style={{
                    minWidth: size * 2,
                    height: size * 2,
                    borderColor: accent,
                    background: on ? accent : "transparent",
                    color: on ? "#FFFFFF" : color,
                  }}
                  onClick={() => {
                    set(v);
                  }}
                >
                  {v}
                </button>
              ) : (
                <span
                  key={v}
                  aria-hidden="true"
                  className="grid place-items-center rounded border-2 font-semibold"
                  style={{ minWidth: size * 2, height: size * 2, borderColor: accent }}
                >
                  {v}
                </span>
              );
            })}
          </div>
          {field.scale.labels ? (
            <div className="mt-0.5 flex justify-between opacity-80" style={{ fontSize: Math.max(9, size - 3) }}>
              <span>{plain(field.scale.labels.min)}</span>
              <span>{plain(field.scale.labels.max)}</span>
            </div>
          ) : null}
          {errorText}
        </fieldset>
      );
    }
    case "text":
    case "textarea":
      // Local-only free text (never measured, never sent). The Studio does not offer it in V1.
      return (
        <label className="grid h-full w-full gap-1" style={box}>
          <span className="font-semibold">{label}</span>
          {live ? (
            field.control === "text" ? (
              <input
                className="rounded border bg-white px-1 text-[#111827]"
                maxLength={field.validation?.maxLength ?? 280}
                onChange={(e) => {
                  set(e.target.value);
                }}
              />
            ) : (
              <textarea
                className="rounded border bg-white px-1 text-[#111827]"
                maxLength={field.validation?.maxLength ?? 280}
                onChange={(e) => {
                  set(e.target.value);
                }}
              />
            )
          ) : (
            <span className="block min-h-6 rounded border bg-white" />
          )}
        </label>
      );
  }
}

// ─── Survey question ───────────────────────────────────────────────────────

export function SurveyQuestionContent({
  el,
  doc,
  mode,
  host,
  focus,
  assetUrl,
}: {
  el: Extract<Element, { type: "component" }>;
  doc: CreativeDocument;
  mode: "editor" | "preview";
  host?: ComponentHost | undefined;
  focus?: EditorFocus | undefined;
  assetUrl: (asset: AssetRef) => string;
}) {
  const props = el.props as unknown as SurveyQuestionProps;
  const survey = doc.survey;
  if (!survey) return null;
  const live = mode === "preview" && host ? host.surveyView() : undefined;
  const editorQ =
    survey.questions.find((q) => q.id === (focus?.question() ?? survey.startQuestionId)) ?? survey.questions[0];
  const q: SurveyQuestion | null | undefined = live ? live.question : editorQ;
  if (!q) return null;
  const color = props.textColor ?? "#111827";
  const accent = props.accentColor ?? "#E4002B";
  const interactive = Boolean(live && host);
  const options =
    q.kind === "scale"
      ? []
      : (live?.order.length ? live.order : q.options.map((o) => o.id)).flatMap((id) => {
          const o = q.options.find((x) => x.id === id);
          return o ? [o] : [];
        });
  const selected = live?.selection ?? [];
  const value = live?.value ?? null;
  const pill = props.optionStyle === "pill";
  const card = props.optionStyle === "card";
  const optionStyle = (on: boolean): CSSProperties => ({
    borderColor: accent,
    background: on ? accent : card ? "#FFFFFF" : "transparent",
    color: on ? "#FFFFFF" : color,
    borderRadius: pill ? 999 : 8,
    borderWidth: props.optionStyle === "plain" ? 0 : 2,
    textDecoration: props.optionStyle === "plain" && on ? "underline" : undefined,
  });
  const showNext = interactive && (q.kind === "multi" || !props.autoAdvance || !q.required);
  const prompt = plain(q.prompt);
  return (
    <div className="flex h-full w-full flex-col gap-2 overflow-hidden" style={{ color }} data-survey-question={q.id}>
      <p id={`${el.id}-prompt`} className="font-semibold" style={{ fontSize: 15, lineHeight: 1.25 }}>
        {prompt}
      </p>
      {q.kind === "scale" ? (
        <div>
          <div
            role={interactive ? "radiogroup" : undefined}
            aria-labelledby={interactive ? `${el.id}-prompt` : undefined}
            className="flex flex-wrap gap-1"
          >
            {Array.from({ length: q.scale.max }, (_, i) => i + 1).map((v) =>
              interactive ? (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={value === v}
                  aria-label={`${String(v)} de ${String(q.scale.max)}`}
                  className="grid min-w-8 place-items-center border-2 px-1 py-1 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-1"
                  style={optionStyle(value === v)}
                  onClick={() => {
                    host?.surveyChoose(v);
                  }}
                >
                  {v}
                </button>
              ) : (
                <span
                  key={v}
                  aria-hidden="true"
                  className="grid min-w-8 place-items-center border-2 px-1 py-1 text-sm font-semibold"
                  style={optionStyle(false)}
                >
                  {v}
                </span>
              )
            )}
          </div>
          {q.scale.labels ? (
            <div className="mt-1 flex justify-between text-xs opacity-80">
              <span>{plain(q.scale.labels.min)}</span>
              <span>{plain(q.scale.labels.max)}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          role={interactive ? (q.kind === "multi" ? "group" : "radiogroup") : undefined}
          aria-labelledby={interactive ? `${el.id}-prompt` : undefined}
          className={props.optionLayout === "grid" ? "grid grid-cols-2 gap-1.5" : "grid gap-1.5"}
        >
          {options.map((o) => {
            const on = selected.includes(o.id);
            const image = o.image ? doc.assets.find((a) => a.key === o.image) : undefined;
            const content = (
              <>
                {image ? <img src={assetUrl(image)} alt="" className="size-8 shrink-0 rounded object-cover" /> : null}
                <span>{plain(o.label)}</span>
              </>
            );
            return interactive ? (
              <button
                key={o.id}
                type="button"
                role={q.kind === "multi" ? "checkbox" : "radio"}
                aria-checked={on}
                className="flex items-center gap-2 border-2 px-3 py-1.5 text-left text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-1"
                style={optionStyle(on)}
                onClick={() => {
                  host?.surveyChoose(o.id);
                }}
              >
                {content}
              </button>
            ) : (
              <span
                key={o.id}
                aria-hidden="true"
                className="flex items-center gap-2 border-2 px-3 py-1.5 text-sm font-medium"
                style={optionStyle(false)}
              >
                {content}
              </span>
            );
          })}
        </div>
      )}
      {q.kind === "multi" && interactive ? <p className="text-xs opacity-80">Elige hasta {q.maxSelections}.</p> : null}
      {showNext ? (
        <button
          type="button"
          className="mt-auto self-end rounded-full px-4 py-1.5 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
          style={{ background: accent, outlineColor: accent }}
          disabled={!host?.surveyCanContinue()}
          onClick={() => {
            host?.surveyNext();
          }}
        >
          {(q.kind === "scale" ? value === null : selected.length === 0) && !q.required ? "Omitir" : "Siguiente"}
        </button>
      ) : null}
    </div>
  );
}

// ─── Progress ──────────────────────────────────────────────────────────────

export function ProgressContent({
  el,
  doc,
  mode,
  host,
}: {
  el: Extract<Element, { type: "component" }>;
  doc: CreativeDocument;
  mode: "editor" | "preview";
  host?: ComponentHost | undefined;
}) {
  const props = el.props as unknown as ProgressProps;
  const color = props.color ?? "#E4002B";
  let done = 0;
  let total = 1;
  if (props.source === "survey") {
    const view = mode === "preview" ? host?.surveyView() : undefined;
    if (view?.completed) {
      done = 1;
      total = 1;
    } else if (view?.question) {
      done = view.position - 1;
      total = done + view.remaining;
    } else total = Math.max(1, doc.survey?.questions.length ? 1 : 1);
  } else if (props.source === "timer" && props.timer) {
    const decl = doc.timers?.find((t) => t.id === props.timer);
    total = decl?.durationMs ?? 1;
    done = mode === "preview" && host ? total - host.timerRemaining(props.timer) : 0;
  } else if (props.source === "variable" && props.variable) {
    total = props.max ?? 100;
    const v = mode === "preview" ? host?.variable(props.variable) : undefined;
    done = typeof v === "number" ? v : 0;
  }
  const ratio = Math.max(0, Math.min(1, total > 0 ? done / total : 0));
  const label = `Progreso: ${String(Math.round(ratio * 100))} %`;
  if (props.style === "dots" && props.source === "survey") {
    const count = Math.min(8, Math.max(1, total));
    return (
      <div
        role="progressbar"
        aria-label="Progreso"
        aria-valuemin={0}
        aria-valuemax={count}
        aria-valuenow={done}
        className="flex h-full w-full items-center justify-center gap-1"
      >
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className="size-2 rounded-full" style={{ background: i < done ? color : `${color}40` }} />
        ))}
      </div>
    );
  }
  if (props.style === "ring") {
    const r = 15.9155;
    return (
      <svg
        viewBox="0 0 36 36"
        className="h-full w-full"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(ratio * 100)}
      >
        <circle cx="18" cy="18" r={r} fill="none" stroke={`${color}40`} strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeDasharray={`${String(ratio * 100)} 100`}
          transform="rotate(-90 18 18)"
        />
      </svg>
    );
  }
  return (
    <div
      role="progressbar"
      aria-label="Progreso"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(ratio * 100)}
      className="h-full w-full overflow-hidden rounded-full"
      style={{ background: `${color}33` }}
    >
      <div
        className="h-full rounded-full"
        style={{ width: `${String(ratio * 100)}%`, background: color, transition: "width 250ms ease" }}
      />
    </div>
  );
}

// ─── Countdown (7.6F) ──────────────────────────────────────────────────────

/**
 * ia.countdown@1: the remaining time of a declared timer. In the preview the
 * digits are rewritten every frame by `syncCountdowns` (no React render per
 * frame); screen readers get the time on demand (role="timer", not live), never
 * a per-second announcement.
 */
export function CountdownContent({
  el,
  doc,
  mode,
  host,
}: {
  el: Extract<Element, { type: "component" }>;
  doc: CreativeDocument;
  mode: "editor" | "preview";
  host?: ComponentHost | undefined;
}) {
  const props = el.props as unknown as CountdownProps;
  const total = doc.timers?.find((t) => t.id === props.timer)?.durationMs ?? 0;
  const ms = mode === "preview" && host ? host.timerRemaining(props.timer) : total;
  const size = props.fontSize ?? Math.max(8, Math.round(el.frame.height * 0.6));
  return (
    <div
      role="timer"
      aria-label={el.interactive?.label ?? "Tiempo restante"}
      className="flex h-full w-full items-center justify-end tabular-nums"
      style={{ color: props.color ?? "#111827", fontSize: size, fontWeight: 700, lineHeight: 1 }}
    >
      <span data-countdown={props.timer} data-format={props.format}>
        {countdownText(ms, props.format)}
      </span>
    </div>
  );
}
